import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { ValidatedIcon, UnvalidatedIcon, ValidatedByOthersIcon } from '../icons/ValidationIcons';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import MarkdownRenderer from '../common/MarkdownRenderer';
import useNotification from '../../hooks/useNotification';
import useDocuments from '../../hooks/useDocuments';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useExplain from '../../hooks/useExplain';
import useImprove from '../../hooks/useImprove';
import useContentActions from '../../hooks/useContentActions';
import useTechStackVersions from '../../hooks/useTechStackVersions';
import { Accordion, ScrollableBox, EditableTextField } from '../atoms';
import VersionSelector from '../atoms/VersionSelector';
// Import full screen functionality
import useFullScreen from '../../hooks/useFullScreen';
import FullScreenButton from '../common/FullScreenButton';
import ConfirmationDialog from '../common/ConfirmationDialog';
import ExplanationModal from '../atoms/ExplanationModal';
import ImprovementModal from '../atoms/ImprovementModal';
import InlineImproveInput from '../common/InlineImproveInput';
import conversationService from '../../services/conversationService';
import LoadMoreButton from '../atoms/LoadMore';
import LoadingBox from '../atoms/LoadingBox';
import TopBarButtons from '../atoms/TopBarButtons';
import SectionHeader from '../atoms/Header';
import EmptyStateBox from '../atoms/EmptyStateBox';
import AddSectionModal from '../atoms/AddSectionModal';
import useParsedSections from '../../hooks/useParsedSections';

/**
 * A split-view layout component that shows conversation on the left
 * and technology stack recommendations in a structured format on the right
 */
const SplitViewTechStack = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [structuredData, setStructuredData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    frontend: true,
    backend: true,
    database: true,
    devops: false,
    thirdParty: false
  });

  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedSections, setExpandedSections);

  const [visibleSections, setVisibleSections] = useState(20); // Show all groups initially
  const [showAddTechModal, setShowAddTechModal] = useState(false);
  const [isSavingTech, setIsSavingTech] = useState(false);

  const { showNotification } = useNotification();
  const { createDocument } = useDocuments();
  const techStackPanelRef = useRef(null);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedSections,
    setSections: setParsedSections,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // Track previous version index and local updates
  const prevVersionIndexRef = useRef(0);
  const isLocalUpdateRef = useRef(false);

  // Use versioning hook for tech stack (pass isLocalUpdateRef for edit sync)
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    addVersion,
    updateCurrentVersion
  } = useTechStackVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);
  // Initialize validation state for tech stack with version-specific IDs
  const initialValidationState = parsedSections.reduce((acc, section) => {
    // Create version-specific validation ID
    const versionSpecificId = `${section.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});


  // Use the validation hook for persistent validation state with version-specific IDs
  const {
    validatedItems: validatedSections,
    handleValidateItem: handleValidateSectionBase,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'tech-stack',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'tech-stack',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateSection = async (sectionId) => {
    // Create version-specific ID
    const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateSectionBase(versionSpecificId);
  };

  // Use CRUD operations hook
  const {
    editingSection,
    editedContent,
    editedTitle,
    setEditedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setDeleteConfirmation,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteSection,
    handleDeleteSectionConfirmed,
    handleCopySection
  } = useSectionCRUD(conversation, parsedSections, setParsedSections, setExpandedSections, contentRef, updateCurrentVersion, currentVersion, isLocalUpdateRef, onConversationUpdate,
    markLocalEdit
  );

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use improve hook
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport: handleExportTechStack,
    getExportContent,
    handleCopy: handleCopyTechStack,
    handleRefresh: handleRefreshTechStack
  } = useContentActions(contentRef, 'Technology Stack', projectId, conversation?._id);

  // Add full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Determine if we should show the split view
  const _taskId = conversation?.taskId || conversation?.task_id;
  const isTechStackConversation = conversation && (
    _taskId === 'tech-stack' ||
    _taskId === 'technology-stack' ||
    _taskId === 'techstack'
  );

  // Version switching: reset visible sections when switching versions
  useEffect(() => {
    if (currentVersion) {
      const versionChanged = prevVersionIndexRef.current !== currentVersionIndex;
      if (versionChanged) {
        setVisibleSections(3);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  // Helper function to update a subsection within a section
  const updateSubsection = (section, subSectionId, indexOfSubSection, newContent) => {
     newContent = newContent
       .replace(/^###\s+\*\*[^*]+\*\*\s*\n*/m, '')  // ### **Title**
       .replace(/^###\s+(?:\d+\.\d+\s+)?[^\n]+\s*\n*/m, '')  // ### 1.1 Title or ### Title
       .trim();
    if(section.subsections[indexOfSubSection]){
      let subSection = section.subsections[indexOfSubSection];
      subSection.content = newContent;
      subSection.rawContent = `### **${subSection.title}**\n\n${newContent}`;
      section.content =  section.subsections.map(sub => sub.rawContent || `### **${sub.title}**\n\n${sub.content}`)
      .join('\n\n');
      return {...section};
    } 
    return section;
  };

 
  // Handler for updating improved content
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;

    try {
      // Handle remove action — delete section from artifact
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        // Trigger a conversation refresh to pick up the change
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }


      // Use merged content if it's a specific section improvement
      const contentToUse = improveSection.mergedContent || improvedContent;
      // Update the section with improved content

      const updatedSections = [...parsedSections].map(s => {
        // Check if this is a top-level section update
        if (s.id === improveSection.id) {
          return {
            ...s,
            content: contentToUse
          };
        }
        // Check if the improved section is a subsection of this section
        const subSectionsIds = s.subsections?.map(sec => sec.id) || [];
        if (subSectionsIds.includes(improveSection.id)) {
         return updateSubsection(s, improveSection.id, subSectionsIds.indexOf(improveSection.id), contentToUse);
        }
        return s;
      });
     console.log('updatedSections', updatedSections);
      setParsedSections(updatedSections);

      // Reconstruct the full content with updated section
      let updatedContent = '';
      updatedSections.forEach(section => {
        updatedContent += `## ${section.title}\n\n${section.content}\n\n`;
      });

      contentRef.current = updatedContent.trim();

      // Save to backend
      const updatedMessages = [...conversation.messages];
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].role === 'assistant') {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent.trim()
          };
          break;
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Notify parent component to refresh conversation (updates left panel)
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      closeImprovementModal();

      showNotification({
        message: 'Section improved successfully',
        type: 'success'
      });

    } catch (error) {
      showNotification({
        message: 'Failed to update improved content',
        type: 'error'
      });
    }
  };

  // Handler for adding new technology
  const handleAddNewTechnology = async (newSectionData) => {
    try {
      // Add to the LAST category (most recently added section)
      const categoryIndex = structuredData?.sections?.length > 0
        ? structuredData.sections.length - 1
        : 0;

      // Generate unique ID for the new technology
      // Use timestamp to ensure uniqueness across all categories
      const timestamp = Date.now();
      const techId = `tech-${timestamp}-${newSectionData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

      const newTechnology = {
        id: techId,
        title: newSectionData.title,
        content: newSectionData.content,
        rawContent: `### **${newSectionData.title}**\n\n${newSectionData.content}`
      };

      // Update the structuredData with new technology
      const updatedSections = [...(structuredData?.sections || [])];
      if (updatedSections.length > 0) {
        // Add to last category's subsections at the END
        updatedSections[categoryIndex] = {
          ...updatedSections[categoryIndex],
          subsections: [
            ...(updatedSections[categoryIndex].subsections || []),
            newTechnology
          ]
        };
      }

      setStructuredData({
        ...structuredData,
        sections: updatedSections
      });

      // Update parsedSections
      setParsedSections(updatedSections);

      // Reconstruct the full content
      let updatedContent = '';
      updatedSections.forEach(section => {
        updatedContent += `## ${section.title}\n\n`;
        if (section.subsections && section.subsections.length > 0) {
          section.subsections.forEach(tech => {
            updatedContent += `### **${tech.title}**\n\n${tech.content}\n\n`;
          });
        } else {
          updatedContent += `${section.content}\n\n`;
        }
      });

      contentRef.current = updatedContent.trim();

      // Save to backend
      const updatedMessages = [...conversation.messages];
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].role === 'assistant') {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent.trim(),
            structured_data: {
              ...structuredData,
              sections: updatedSections
            }
          };
          break;
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString(),
          structured_data: msg.structured_data
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Mark as local update
      isLocalUpdateRef.current = true;

      // Update current version
      if (currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          sections: updatedSections,
          content: updatedContent.trim()
        });
      }

      // Notify parent component to refresh conversation (updates left panel)
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      setShowAddTechModal(false);

      showNotification({
        message: 'Technology added successfully',
        type: 'success'
      });

    } catch (error) {
      showNotification({
        message: 'Failed to add technology',
        type: 'error'
      });
    }
  };

  // Custom delete handler for technologies (handles nested structure)
  const handleDeleteTechnology = async (technology) => {

    try {
      // Find which category contains this technology
      const updatedSections = parsedSections.map(section => {
        if (section.subsections) {
          const beforeCount = section.subsections.length;
          const filteredSubsections = section.subsections.filter(tech => tech.id !== technology.id);
          const afterCount = filteredSubsections.length;

          // Only return updated section if subsections changed
          if (filteredSubsections.length !== section.subsections.length) {
            return {
              ...section,
              subsections: filteredSubsections
            };
          }
        }
        return section;
      });


      // Mark as local update
      isLocalUpdateRef.current = true;

      // Update state
      setParsedSections(updatedSections);

      setStructuredData({
        type: 'tech-stack',
        sections: updatedSections
      });

      // Remove expanded state for this technology
      setExpandedSections(prev => {
        const updated = { ...prev };
        delete updated[technology.id];
        return updated;
      });

      // Reconstruct the full content
      let updatedContent = '';
      updatedSections.forEach(section => {
        updatedContent += `## ${section.title}\n\n`;
        if (section.subsections && section.subsections.length > 0) {
          section.subsections.forEach(tech => {
            updatedContent += `### **${tech.title}**\n\n${tech.content}\n\n`;
          });
        } else {
          updatedContent += `${section.content}\n\n`;
        }
      });

      contentRef.current = updatedContent.trim();

      // Save to backend
      const updatedMessages = [...conversation.messages];
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].role === 'assistant') {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent.trim(),
            structured_data: {
              type: 'tech-stack',
              sections: updatedSections
            }
          };
          break;
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString(),
          structured_data: msg.structured_data
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update current version
      if (currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          sections: updatedSections,
          content: updatedContent.trim()
        });
      }

      // Notify parent component to refresh conversation (updates left panel)
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      showNotification({
        message: `"${technology.title}" deleted successfully in version ${currentVersionIndex + 1}`,
        type: 'success'
      });

    } catch (error) {
      console.error('Error deleting technology:', error);
      showNotification({
        message: `Failed to delete "${technology.title}" in version ${currentVersionIndex + 1}`,
        type: 'error'
      });
    }
  };

  // Custom save handler for technologies (handles nested structure)
  const handleSaveTechnology = async (techId) => {
    try {
      setIsSavingTech(true);

      // Find which category contains this technology and update it
      let technologyFound = false;
      const updatedSections = parsedSections.map(section => {
        if (section.subsections) {
          const techIndex = section.subsections.findIndex(tech => tech.id === techId);
          if (techIndex !== -1) {
            technologyFound = true;
            const updatedSubsections = [...section.subsections];
            // Use editedTitle if provided, otherwise keep original title
            const newTitle = editedTitle || updatedSubsections[techIndex].title;
            updatedSubsections[techIndex] = {
              ...updatedSubsections[techIndex],
              title: newTitle,
              content: editedContent,
              rawContent: `### **${newTitle}**\n\n${editedContent}`
            };
            return {
              ...section,
              subsections: updatedSubsections
            };
          }
        }
        return section;
      });

      if (!technologyFound) {
        console.error('Technology not found:', techId);
        return;
      }

      // Mark as local update
      isLocalUpdateRef.current = true;

      // Update state
      setParsedSections(updatedSections);
      setStructuredData({
        type: 'tech-stack',
        sections: updatedSections
      });

      // Reconstruct the full content
      let updatedContent = '';
      updatedSections.forEach(section => {
        updatedContent += `## ${section.title}\n\n`;
        if (section.subsections && section.subsections.length > 0) {
          section.subsections.forEach(tech => {
            updatedContent += `### **${tech.title}**\n\n${tech.content}\n\n`;
          });
        } else {
          updatedContent += `${section.content}\n\n`;
        }
      });

      contentRef.current = updatedContent.trim();

      // Save to backend
      const updatedMessages = [...conversation.messages];
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].role === 'assistant') {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent.trim(),
            structured_data: {
              type: 'tech-stack',
              sections: updatedSections
            }
          };
          break;
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString(),
          structured_data: msg.structured_data
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update current version
      if (currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          sections: updatedSections,
          content: updatedContent.trim()
        });
      }

      // Notify parent component to refresh conversation (updates left panel)
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      // Exit edit mode using the hook's cancel function
      handleCancelEdit();

      showNotification({
        message: 'Technology updated successfully',
        type: 'success'
      });

    } catch (error) {
      console.error('Error saving technology:', error);
      showNotification({
        message: 'Failed to save changes',
        type: 'error'
      });
    } finally {
      setIsSavingTech(false);
    }
  };

  // Custom handlers to override useSectionCRUD delete handlers
  const handleDeleteTechnologyClick = (technology) => {
    setDeleteConfirmation({ open: true, section: technology });
  };

  const handleDeleteTechnologyConfirmed = async () => {
    const technology = deleteConfirmation.section;
    setDeleteConfirmation({ open: false, section: null });

    if (!technology) return;

    await handleDeleteTechnology(technology);
  };

  // Extract structured data from conversation (provided by backend)
  const extractStructuredData = () => {
    // Check if the latest assistant message has structured data
    const assistantMessages = conversation?.messages?.filter(msg => msg.role === 'assistant') || [];
    const latestMessage = assistantMessages[assistantMessages.length - 1];

    // Look for structured_data in the latest message or conversation
    if (latestMessage?.structured_data) {
      return latestMessage.structured_data;
    }

    // Check if conversation has structured_data
    if (conversation?.structured_data) {
      return conversation.structured_data;
    }

    return null;
  };

  // Sync structuredData from parsedSections (backend source of truth).
  // parsedSections comes from useParsedSections → /parsed-responses/latest endpoint.
  // structuredData is what the render loop uses.
  // NOTE: The render now groups parsedSections inline (same as Architecture).
  // No useEffect needed for structuredData — it's only kept for add-technology handler.
  useEffect(() => {
    if (!isTechStackConversation) return;
    if (parsedSections.length > 0) {
      setStructuredData({ type: 'tech-stack', sections: parsedSections });
    }
  }, [parsedSections, isTechStackConversation]);

  // Function to parse tech stack content into hierarchical structure
  // Handles multiple formats:
  // 1. "## 1. Frontend Technology Stack" with "### 1.1 Core Framework" subsections
  // 2. "## 1. Frontend Technology Stack" with "### Core Framework" subsections
  // 3. "# Technology Stack" with "### 1.1 Core Framework" subsections (flat structure)
  const parseTechStackContent = (content) => {

    const sections = [];

    // Split content by main section headers - look for ## followed by number
    const mainSectionSplits = content.split(/(?=^##\s+\d+\.)/gm);
    const hasNumberedSections = mainSectionSplits.some(part => part.trim().match(/^##\s+\d+\./));
    if (hasNumberedSections) {
      // Standard format: ## 1. Frontend Technology Stack
      mainSectionSplits.forEach((part, index) => {

        // Skip empty parts or parts that don't start with numbered section
        if (!part.trim() || !part.match(/^##\s+\d+\./)) {
          return;
        }

        // Extract section header and content
        const lines = part.trim().split('\n');
        const headerLine = lines[0];
        const remainingContent = lines.slice(1).join('\n');

        // Parse header: "## 1. Frontend Technology Stack"
        const headerMatch = headerLine.match(/^##\s+(\d+)\.\s+(.+)$/);
        if (!headerMatch) {
          return;
        }

        const sectionNumber = headerMatch[1];
        const sectionTitle = headerMatch[2].trim();

        // Determine section type
        let sectionType = 'general';
        const titleLower = sectionTitle.toLowerCase();
        if (titleLower.includes('frontend')) sectionType = 'frontend';
        else if (titleLower.includes('backend')) sectionType = 'backend';
        else if (titleLower.includes('database')) sectionType = 'database';
        else if (titleLower.includes('devops') || titleLower.includes('infrastructure')) sectionType = 'devops';
        else if (titleLower.includes('third') || titleLower.includes('service')) sectionType = 'third-party';
        else if (titleLower.includes('development') || titleLower.includes('tool')) sectionType = 'devops';

        // Parse subsections within this main section
        const subsections = parseSubsections(remainingContent.trim());
        // Generate unique ID for the section
        const sectionId = `tech-section-${sectionNumber}-${sectionType}`;

        sections.push({
          id: sectionId,
          title: `${sectionNumber}. ${sectionTitle}`,
          content: remainingContent.trim(),
          type: sectionType,
          subsections: subsections
        });
      });
    }

    // If no numbered sections found, try flat format with ### subsections
    if (sections.length === 0) {
      // Flat format: Content has ### subsections but no ## numbered sections
      // This happens when AI generates "# Technology Stack" followed directly by ### subsections
      const subsections = parseSubsections(content);

      if (subsections.length > 0) {
        // Try to extract a main title from # header
        const titleMatch = content.match(/^#\s+(.+?)$/m);
        const mainTitle = titleMatch ? titleMatch[1].trim() : 'Technology Stack';

        // Determine section type from title
        let sectionType = 'general';
        const titleLower = mainTitle.toLowerCase();
        if (titleLower.includes('frontend')) sectionType = 'frontend';
        else if (titleLower.includes('backend')) sectionType = 'backend';
        else if (titleLower.includes('database')) sectionType = 'database';
        else if (titleLower.includes('devops') || titleLower.includes('infrastructure')) sectionType = 'devops';

        sections.push({
          id: 'tech-section-1-general',
          title: mainTitle,
          content: content,
          type: sectionType,
          subsections: subsections
        });
      } else {
        // Last resort fallback - just show the content
        let fallbackContent = content
          .replace(/^#.*?Technology Stack.*?\n/i, '')
          .replace(/^##.*?Enhanced Transportation Efficiency.*?\n/i, '')
          .replace(/^---+\s*\n/gm, '')
          .replace(/^## Executive Summary[\s\S]*?(?=^## \d|^---|\n\n##)/gm, '')
          .trim();

        if (fallbackContent) {
          sections.push({
            id: 'tech-section-1-general',
            title: 'Technology Stack',
            content: fallbackContent,
            type: 'general',
            subsections: []
          });
        }
      }
    }

    const result = {
      type: 'tech-stack',
      sections: sections,
      metadata: {
        total_sections: sections.length,
        has_subsections: sections.some(s => s.subsections && s.subsections.length > 0)
      }
    };
    return result;
  };

  // Function to parse technologies (### headers with ** bold titles)
  const parseSubsections = (content) => {
    const subsections = [];

    // Split by ### headers to get individual technologies
    // Handle both formats: ### **Name** and ### X.X Name
    const subsectionSplits = content.split(/(?=^###\s+)/gm);

    subsectionSplits.forEach((part, index) => {
      // Skip empty parts
      if (!part.trim()) {
        return;
      }

      // Try to match ### **Technology Name** format first
      let headerMatch = part.match(/^###\s+\*\*([^*]+)\*\*\s*/);
      let technologyTitle = null;
      let remainingContent = null;

      if (headerMatch) {
        // Format: ### **Technology Name**
        technologyTitle = headerMatch[1].trim();
        remainingContent = part.substring(headerMatch[0].length).trim();
      } else {
        // Try to match ### X.X Title format (e.g., ### 1.1 Core Framework)
        headerMatch = part.match(/^###\s+(?:\d+\.\d+\s+)?(.+?)$/m);
        if (headerMatch) {
          technologyTitle = headerMatch[1].trim();
          // Get content after the first line
          const lines = part.split('\n');
          remainingContent = lines.slice(1).join('\n').trim();
        } else {
          return;
        }
      }

      // Generate unique ID for this technology
      const techId = `tech-${index}-${technologyTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

      subsections.push({
        id: techId,
        title: technologyTitle,
        content: remainingContent,
        rawContent: part.trim()
      });
    });
    return subsections;
  };

  // Auto-scroll to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && techStackPanelRef.current && isTechStackConversation) {
      // Wait a bit to ensure content renders before scrolling
      setTimeout(() => {
        if (techStackPanelRef.current) {
          techStackPanelRef.current.scrollTop = techStackPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isTechStackConversation]);


  // Don't show the split view if this isn't a tech-stack conversation
  if (!isTechStackConversation) {
    return <>{children}</>;
  }

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      mt: 5
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(sidebarCollapsed),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Tech stack panel */}
        <Box
          ref={techStackPanelRef}
          sx={{
            ...getRightPanelStyles(sidebarCollapsed),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <SectionHeader label='Technology stack' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Version Selector */}
                {hasVersions && versions.length > 1 && (
                  <Box sx={{ mt: 2, px: 2 }}>
                    <VersionSelector
                      versions={versions}
                      currentVersionIndex={currentVersionIndex}
                      onVersionChange={(newIndex) => {
                        isLocalUpdateRef.current = false;
                        setCurrentVersionIndex(newIndex);
                      }}
                      disabled={isStreaming}
                    />
                  </Box>
                )}
                {/* Fixed header section */}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  onAdd={(!hasVersions || isLatestVersion) ? () => setShowAddTechModal(true) : null}
                  getExportContent={getExportContent}
                  onExport={handleExportTechStack}
                  isExporting={isExporting}
                  onCopy={handleCopyTechStack}
                  onRefresh={() => {
                    showNotification({
                      message: "Refreshing tech stack...",
                      type: "info"
                    });
                  }}
                  disabled={!contentRef.current}
                  addTooltip={(hasVersions && !isLatestVersion) ? "Switch to latest version to add technologies" : "Add new technology"}
                  exportTooltip="Export tech stack as markdown"
                  copyTooltip="Copy tech stack to clipboard"
                  refreshTooltip="Refresh tech stack"
                />
              </Box>


            </Box>

            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'tech-stack'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedSections}
              totalItems={parsedSections?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>

              {/* Render tech stack — same pattern as Architecture */}
              {isStreaming ? (
                <LoadingBox message="Generating technology stack recommendations..." spinnerSize={24} />
              ) : parsedSections.length > 0 ? (
                <Box>
                  {(() => {
                    // Filter out non-tech-stack sections (context overviews, questions, etc.)
                    // Real tech-stack sections either: have "Primary Recommendation" in content,
                    // are known structural sections, or are category headers for numbered subsections.
                    const STRUCTURAL_TITLES = ['project analysis', 'integration map', 'risk assessment'];
                    const filteredSections = parsedSections.filter(s => {
                      const content = s.content || '';
                      const title = (s.title || '').toLowerCase();
                      // Keep sections with tech-stack content markers
                      if (content.includes('Primary Recommendation') || content.includes('Rationale:')) return true;
                      if (content.includes('Alternatives Considered') || content.includes('Community Health')) return true;
                      // Keep known structural sections
                      if (STRUCTURAL_TITLES.some(t => title.includes(t))) return true;
                      // Keep category headers (they group numbered subsections)
                      if (s.isCategory && parsedSections.some(c => c.category === s.title && !c.isCategory)) return true;
                      // Keep numbered sections (N.N format)
                      if (/^\d+\.\d+/.test(s.title)) return true;
                      return false;
                    });

                    // Group filtered sections by category
                    const groups = [];
                    let currentGroup = null;
                    filteredSections.forEach((section) => {
                      if (section.isCategory || section.isFirstInCategory || !currentGroup) {
                        currentGroup = {
                          category: section.category || section.title,
                          categoryId: section.isCategory ? section.id : `cat-${section.id}`,
                          sections: section.isCategory ? [] : [section],
                          categorySection: section.isCategory && section.content ? section : null,
                        };
                        groups.push(currentGroup);
                      } else {
                        currentGroup.sections.push(section);
                      }
                    });

                    let globalIndex = 0;
                    const rendered = groups.slice(0, visibleSections).map((group, groupIndex) => (
                      <Box key={group.categoryId} sx={{
                        mb: 4,
                        pb: 2,
                        borderBottom: groupIndex < groups.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                      }}>
                        {/* Standalone category with content but no children → render as accordion */}
                        {group.categorySection && group.sections.length === 0 ? (
                          <Box sx={{ mt: groupIndex === 0 ? 0 : 1, mb: 2 }}>
                            {(() => {
                              const catSection = group.categorySection;
                              const catSectionId = catSection.id;
                              const catVersionId = `${catSectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                              const catIsValidated = validatedSections[catVersionId] || false;
                              const catIdx = globalIndex++;
                              return (
                                <Accordion
                                  expanded={expandedSections[catSectionId] || false}
                                  onChange={() => setExpandedSections(prev => ({ ...prev, [catSectionId]: !prev[catSectionId] }))}
                                  expandIcon={false}
                                  sx={{ mb: 1.5, '&.Mui-expanded': { margin: 0, mb: 1.5 } }}
                                  summaryContent={
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "15px", textTransform: 'none !important' }}>
                                        {catSection.title}
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: "column", justifyContent: "center" }}>
                                        <Box onClick={async (e) => {
                                          e.stopPropagation();
                                          try { await handleValidateSection(catSectionId); } catch (error) {
                                            showNotification({ message: `Failed to validate "${catSection.title}"`, type: "error" });
                                          }
                                        }}
                                          sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}>
                                          {catIsValidated ? <ValidatedIcon size={25} memberType={getValidatorType(catVersionId)} /> : <UnvalidatedIcon size={25} />}
                                        </Box>
                                        <Box sx={{ display: 'flex', transform: expandedSections[catSectionId] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 150ms' }}>
                                          <ExpandMoreIcon fontSize="small" />
                                        </Box>
                                      </Box>
                                    </Box>
                                  }
                                >
                                  <ScrollableBox sx={{ textAlign: 'justify' }}>
                                    <Box sx={{ fontSize: '16px' }}>
                                      <MarkdownRenderer content={catSection.content || ''} />
                                    </Box>
                                  </ScrollableBox>
                                  <Box sx={{ position: 'absolute', right: 10, top: 10, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <ActionButtons
                                      item={catSection}
                                      itemIndex={catIdx}
                                      featureId={catSectionId}
                                      editingFeature={editingSection}
                                      isSaving={isSaving}
                                      onEdit={() => handleStartEdit(catSectionId, catSection.content || '', catSection.title || '')}
                                      onSave={() => handleSaveEdit(catSectionId)}
                                      onCancel={handleCancelEdit}
                                      onImprove={() => handleImproveClick(catSection)}
                                      onExplain={() => requestExplanation(catSection, projectId)}
                                      onCopy={() => handleCopySection(catSection)}
                                      vertical={true}
                          onComment={() => openCommentPanel(catSectionId)}
                          commentCount={commentCounts[catSectionId]?.open || 0}
                                      isApproved={catIsValidated}
                                    />
                                  </Box>
                                </Accordion>
                              );
                            })()}
                          </Box>
                        ) : (
                          <>
                            {/* Category Header (has children) */}
                            <Box sx={{ mt: groupIndex === 0 ? 0 : 3, mb: 2 }}>
                              <SectionHeader label={group.category} />
                            </Box>

                            {/* Category intro content — clean metadata block */}
                            {group.categorySection && (
                              <Box sx={{ pl: 2, mb: 2 }}>
                                <Box sx={{
                                  p: 2,
                                  backgroundColor: 'rgba(95, 138, 143, 0.04)',
                                  borderRadius: 1,
                                  borderLeft: '3px solid #5f8a8f',
                                  fontSize: '13px',
                                  '& p': { mb: 0.5, '&:last-child': { mb: 0 } },
                                  '& strong': { color: '#3A4754', display: 'block', mt: 0.5 },
                                  '& li': { mb: 0.25 },
                                }}>
                                  <MarkdownRenderer content={
                                    (group.categorySection.content || '')
                                      .replace(/\*\*([A-Z][^*]+):\*\*/g, '\n**$1:**')
                                      .trim()
                                  } />
                                </Box>
                              </Box>
                            )}
                          </>
                        )}

                        {/* Child sections as accordions */}
                        <Box sx={{ pl: 2 }}>
                          {group.sections.map((section) => {
                            const idx = globalIndex++;
                            const sectionId = section.id;
                            const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                            const isValidated = validatedSections[versionSpecificId] || false;

                            return (
                              <React.Fragment key={section.id}>
                                <Accordion
                                  ref={shouldHighlight(section.id) ? highlightRef : null}
                                  expanded={expandedSections[section.id] || false}
                                  onChange={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
                                  expandIcon={false}
                                  sx={{ mb: 1.5, '&.Mui-expanded': { margin: 0, mb: 1.5 } }}
                                  summaryContent={
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                      {editingSection === section.id ? (
                                        <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                                          <TextField
                                            value={editedTitle}
                                            onChange={(e) => setEditedTitle(e.target.value)}
                                            onKeyDown={(e) => e.stopPropagation()}
                                            size="small"
                                            fullWidth
                                            placeholder="Enter section title"
                                            variant="outlined"
                                            sx={{
                                              '& .MuiOutlinedInput-root': { fontSize: '14px', backgroundColor: '#fff' },
                                              '& .MuiOutlinedInput-input': { fontSize: '14px', padding: '8px 12px' },
                                            }}
                                          />
                                        </Box>
                                      ) : (
                                        <Box>
                                          <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "15px", textTransform: 'none !important' }}>
                                            {section.title}
                                          </Typography>
                                          {/* Show Stack Category & Subcategory chips */}
                                          {(() => {
                                            const content = section.content || '';
                                            const catMatch = content.match(/\*\*Stack Category:\*\*\s*(.+)/);
                                            const subMatch = content.match(/\*\*Subcategory:\*\*\s*(.+)/);
                                            const recMatch = content.match(/\*\*Primary Recommendation:\*\*\s*(.+)/);
                                            if (!catMatch && !subMatch && !recMatch) return null;
                                            return (
                                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                                {catMatch && (
                                                  <Typography variant="caption" sx={{ color: '#5f8a8f', fontSize: '11px', fontWeight: 500 }}>
                                                    {catMatch[1].trim()}
                                                  </Typography>
                                                )}
                                                {subMatch && (
                                                  <Typography variant="caption" sx={{ color: '#6B757F', fontSize: '11px' }}>
                                                    {catMatch ? ' · ' : ''}{subMatch[1].trim()}
                                                  </Typography>
                                                )}
                                                {recMatch && (
                                                  <Typography variant="caption" sx={{ color: '#475969', fontSize: '11px', fontWeight: 600 }}>
                                                    {(catMatch || subMatch) ? ' · ' : ''}{recMatch[1].trim().replace(/\*\*/g, '')}
                                                  </Typography>
                                                )}
                                              </Box>
                                            );
                                          })()}
                                        </Box>
                                      )}
                                      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: "column", justifyContent: "center" }}>
                                        <Box onClick={async (e) => {
                                          e.stopPropagation();
                                          try { await handleValidateSection(sectionId); } catch (error) {
                                            showNotification({ message: `Failed to validate "${section.title}"`, type: "error" });
                                          }
                                        }}
                                          sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}>
                                          {isValidated ? <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} /> : <UnvalidatedIcon size={25} />}
                                        </Box>
                                        <Box sx={{ display: 'flex', transform: expandedSections[section.id] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 150ms' }}>
                                          <ExpandMoreIcon fontSize="small" />
                                        </Box>
                                      </Box>
                                    </Box>
                                  }
                                >
                                  <ScrollableBox sx={{ textAlign: 'justify', cursor: editingSection === section.id ? 'default' : 'auto' }}>
                                    {editingSection === section.id ? (
                                      <EditableTextField value={editedContent} onChange={(e) => setEditedContent(e.target.value)} />
                                    ) : (
                                      <Box
                                        ref={getSectionContentRef(section.id)}
                                        sx={{ fontSize: '16px', position: 'relative' }}
                                      >
                                        <MarkdownRenderer content={section.content || ''} />
                                        <TextSelectionPopup
                                          containerRef={getSectionContentRef(section.id)}
                                          sectionId={section.id}
                                          onSubmitComment={addComment}
                                          disabled={editingSection === section.id}
                                        />
                                      </Box>
                                    )}
                                  </ScrollableBox>
                                  <Box sx={{ position: 'absolute', right: 10, top: 10, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <ActionButtons
                                      item={section}
                                      itemIndex={idx}
                                      featureId={section.id}
                                      editingFeature={editingSection}
                                      isSaving={isSaving}
                                      onEdit={() => handleStartEdit(section.id, section.content || '', section.title || '')}
                                      onSave={() => handleSaveEdit(section.id)}
                                      onCancel={handleCancelEdit}
                                      onImprove={() => handleImproveClick(section)}
                                      onExplain={() => requestExplanation(section, projectId)}
                                      onCopy={() => handleCopySection(section)}
                                      onDelete={() => handleDeleteSection(section)}
                                      vertical={true}
                          onComment={() => openCommentPanel(section.id)}
                          commentCount={commentCounts[section.id]?.open || 0}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                                    />
                                  </Box>
                                </Accordion>
                                {showImproveInput === section.id && (
                                  <InlineImproveInput
                                    improvePrompt={improvePrompt}
                                    onPromptChange={setImprovePrompt}
                                    onSend={() => handleSendImprove(section)}
                                    onCancel={handleCancelImprove}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      </Box>
                    ));

                    // Load more button
                    const shouldShowMore = groups.length > visibleSections;
                    const remaining = groups.length - visibleSections;
                    if (shouldShowMore) {
                      rendered.push(
                        <LoadMoreButton
                          key="load-more"
                          remaining={remaining}
                          onLoadMore={() => setVisibleSections(v => Math.min(v + 3, groups.length))}
                          onShowAll={() => setVisibleSections(groups.length)}
                          label="Load More Sections"
                        />
                      );
                    }
                    return rendered;
                  })()}
                </Box>
              ) : (
                <EmptyStateBox message="No technology stack content found yet. Start a conversation to generate recommendations." />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        title="Delete Technology"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height="auto"
        onConfirm={handleDeleteTechnologyConfirmed}
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedContent}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />

      {/* Add Technology Modal */}
      <AddSectionModal
        open={showAddTechModal}
        onClose={() => setShowAddTechModal(false)}
        onSave={handleAddNewTechnology}
        sectionType="technology"
        existingSections={parsedSections}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedSections || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewTechStack;